import { marqueeImages } from "@/data/marquee-images";
import {
    getItemData,
    containsFocusVisible,
    focusDidLeave,
    type MarqueeItemData,
} from "./utils";
import s from "./style.module.css";
// Stable variables
<const/itemRefs: (() => HTMLElement)[]=[]>
<const/refs={ trackX: 0 }>
// Stateful variables
<let/isInView=false>
<let/systemPaused=false>
<let/userPaused=false>
<let/trackWidth=0>
<let/speed=0.5>
<let/itemData=([] as MarqueeItemData[])>
// Derived variables
<const/shouldAnimate=isInView && !systemPaused && !userPaused>

<content-section paddingInline=false fullScreen paddingBlock=["none", "xl"]>
    <div/marqueeRef class=s.marquee id="marquee">
        <ul/trackRef
            class=s.track
            id="track"
            onFocusIn(e) {
                const el = e.target as HTMLElement;
                if (el.matches(":focus-visible")) {
                    systemPaused = true;
                }
            }
            onFocusOut(e: FocusEvent) {
                // Only restart animation if focus leaves,
                // not if focus moves within
                if (focusDidLeave(e, trackRef())) {
                    systemPaused = false;
                }
            }>
            <for|image, i| of=marqueeImages>
                <li/itemRef
                    class=s.item
                    data-marquee-item
                    style=`aspect-ratio: ${image.aspectRatio}`>
                    <lightbox-image
                        ...image
                        index=i
                        onOpen() {
                            systemPaused = true;
                        }
                        onClose() {
                            systemPaused = false;
                        }/>
                </li>
                <script>
                    itemRefs.push(itemRef);
                </script>
            </for>
        </ul>
        <div class=s.controls>
            <div>
                <play-button
                    aria-label=`${shouldAnimate ? "Pause" : "Resume"} marquee`
                    paused=!shouldAnimate
                    class=s.button
                    onClick() {
                        userPaused = !userPaused;
                    }/>
            </div>
        </div>
    </div>
</content-section>

<script>
    // Local vars -------------------------------------
    const container = marqueeRef();
    const track = trackRef();
    let rafId: any = null;

    // Animate ----------------------------------------
    if (shouldAnimate) tick();
    else update();

    // Utils ------------------------------------------
    function update() {
        refs.trackX -= speed;

        // 1. The Reset Logic
        // If the track has moved its entire length, reset trackX to 0.
        if (Math.abs(refs.trackX) >= trackWidth) {
            refs.trackX += trackWidth;

            // When the track resets, we must also reset all item offsets
            itemData.forEach((item) => {
                item.offsetX = 0;
                item.ref().style.transform = `translate3d(0, 0, 0)`;
            });
        }
        itemData.forEach((item) => {
            // Current position = Track position + Natural position + Teleport Offset
            // item.el.offsetLeft gives us its natural position in the flex row
            const currentGlobalX =
                refs.trackX + item.ref().offsetLeft + item.offsetX;

            // 2. The Teleport Logic
            // If an item is off-screen to the left, shift it right by totalWidth
            if (currentGlobalX + item.width < 0) {
                item.offsetX += trackWidth;
                item.ref().style.transform = `translate3d(${item.offsetX}px, 0, 0)`;
            }

            // 3. Accessibility: Visibility Check
            const itemOffset = item.width * 0.666;
            const isVisible =
                currentGlobalX >= -1 * itemOffset &&
                currentGlobalX + item.width <=
                    container.offsetWidth + itemOffset;
            item.ref().inert = isVisible ? false : true;
            item.ref().dataset.visible = `${isVisible}`;
        });

        // Move the entire track
        track.style.transform = `translate3d(${refs.trackX}px, 0, 0)`;
    }
    //
    function tick() {
        if (!containsFocusVisible(track)) {
            update();
            rafId = requestAnimationFrame(tick);
        }
    }
    function cancel() {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = null;
    }
    $signal.onabort = () => {
        cancel();
    };
</script>
<script>
    // Initial setup -----------------------------------
    init();
    userPaused = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Local vars --------------------------------------
    const container = marqueeRef();
    let prevWidth = 0;

    // Setup logic -------------------------------------
    function init() {
        refs.trackX = 0;
        trackWidth = trackRef().getBoundingClientRect().width;
        itemData = getItemData(itemRefs);
        speed = Math.min(window.innerWidth / 800, 1.25);
    }

    // Resize ---------------------------------------
    function handleResize() {
        const currentWidth = window.innerWidth;
        if (currentWidth === prevWidth) return;
        prevWidth = currentWidth;
        init();
    }
    window.addEventListener("resize", handleResize, {
        signal: $signal,
    });

    // Visibility ---------------------------------------
    const observer = new IntersectionObserver(([entry]) => {
        isInView = entry.isIntersecting;
    });
    observer.observe(container);
    $signal.onabort = () => {
        observer.unobserve(container);
        observer.disconnect();
    };
</script>
